import { getApiBaseUrl, requestJson } from "@/lib/api";
import type {
  ClientType,
  PriceChange,
  PriceTable,
  PriceTableChildSummary,
  PriceTableDetail,
  PriceTableEntry,
  PriceTableLinkedCustomer,
} from "@/lib/types";

interface PriceTableSummaryDto {
  id: string;
  name: string;
  scope: ClientType;
  active: boolean;
  createdAt: string;
  updatedAt: string;
  entryCount: number;
  linkedCustomerCount: number;
  linkedDeliveryUnitCount?: number;
  parentId?: string | null;
  parentName?: string | null;
  markupPercent?: number | null;
  childCount?: number;
  stale?: boolean;
  defaultBase?: boolean;
}

interface PriceTableEntryDto {
  id?: string;
  productId: string;
  unitId: string;
  cutTypeId?: string | null;
  cutName?: string | null;
  price: number;
  isOverride?: boolean;
}

interface PriceChangeDto {
  oldPrice: number;
  newPrice: number;
  changedAt: string;
  actorEmail: string;
  actorRole: string;
}

interface PriceTableDetailDto extends PriceTableSummaryDto {
  entries: PriceTableEntryDto[];
  linkedCustomers: PriceTableLinkedCustomerDto[];
  linkedDeliveryUnits?: PriceTableLinkedDeliveryUnitDto[];
  children: PriceTableChildSummary[];
}

interface PriceTableLinkedCustomerDto {
  id: string;
  companyName?: string | null;
  email: string;
  status: "PENDENTE_VALIDACAO" | "VALIDADO" | "REJEITADO" | "BLOQUEADO";
}

interface PriceTableLinkedDeliveryUnitDto {
  id: string;
  name: string;
  customerId: string;
  customerName: string;
  active: boolean;
}

interface PriceTableUpsertDto {
  name: string;
  scope: ClientType;
  active: boolean;
  duplicateFromId?: string;
  entries?: PriceTableEntryDto[];
}

function toPriceTable(dto: PriceTableSummaryDto): PriceTable {
  return {
    id: dto.id,
    name: dto.name,
    scope: dto.scope,
    active: dto.active,
    createdAt: dto.createdAt,
    updatedAt: dto.updatedAt,
    entryCount: dto.entryCount,
    linkedCustomerCount: dto.linkedCustomerCount,
    linkedDeliveryUnitCount: dto.linkedDeliveryUnitCount ?? 0,
    parentId: dto.parentId ?? null,
    parentName: dto.parentName ?? null,
    markupPercent: dto.markupPercent ?? null,
    childCount: dto.childCount ?? 0,
    stale: dto.stale ?? false,
    defaultBase: dto.defaultBase ?? false,
  };
}

function toPriceTableDetail(dto: PriceTableDetailDto): PriceTableDetail {
  return {
    ...toPriceTable(dto),
    entries: dto.entries.map((e) => ({ id: e.id, productId: e.productId, unitId: e.unitId, cutTypeId: e.cutTypeId ?? null, cutName: e.cutName ?? null, price: e.price, isOverride: e.isOverride ?? false })),
    linkedCustomers: dto.linkedCustomers.map(toLinkedCustomer),
    linkedDeliveryUnits: (dto.linkedDeliveryUnits ?? []).map(toLinkedDeliveryUnit),
    children: dto.children ?? [],
  };
}

function toLinkedDeliveryUnit(unit: PriceTableLinkedDeliveryUnitDto) {
  return {
    id: unit.id,
    name: unit.name,
    customerId: unit.customerId,
    customerName: unit.customerName,
    active: unit.active,
  };
}

function toLinkedCustomer(customer: PriceTableLinkedCustomerDto): PriceTableLinkedCustomer {
  return {
    id: customer.id,
    companyName: customer.companyName ?? undefined,
    email: customer.email,
    status: customer.status,
  };
}

function toEntries(entries: PriceTableEntry[]): PriceTableEntryDto[] {
  return entries.map((e) => ({ productId: e.productId, unitId: e.unitId, cutTypeId: e.cutTypeId ?? null, price: e.price, isOverride: e.isOverride ?? false }));
}

export async function fetchPriceTables(): Promise<PriceTable[]> {
  const priceTables = await requestJson<PriceTableSummaryDto[]>("/api/v1/admin/price-tables");
  return priceTables.map(toPriceTable);
}

export async function fetchPriceTable(id: string): Promise<PriceTableDetail> {
  const priceTable = await requestJson<PriceTableDetailDto>(`/api/v1/admin/price-tables/${id}`);
  return toPriceTableDetail(priceTable);
}

export async function createPriceTable(input: {
  name: string;
  scope: ClientType;
  active: boolean;
  duplicateFromId?: string;
  entries?: PriceTableEntry[];
}): Promise<PriceTableDetail> {
  const priceTable = await requestJson<PriceTableDetailDto>("/api/v1/admin/price-tables", {
    method: "POST",
    body: {
      ...input,
      entries: input.entries ? toEntries(input.entries) : undefined,
    } satisfies PriceTableUpsertDto,
  });
  return toPriceTableDetail(priceTable);
}

export async function createChildTable(parentId: string, input: {
  name: string;
  markupPercent: number;
}): Promise<PriceTableDetail> {
  return toPriceTableDetail(
    await requestJson<PriceTableDetailDto>(`/api/v1/admin/price-tables/${parentId}/children`, {
      method: "POST",
      body: input,
    })
  );
}

export async function propagateToChildren(parentId: string): Promise<PriceTableDetail> {
  return toPriceTableDetail(
    await requestJson<PriceTableDetailDto>(`/api/v1/admin/price-tables/${parentId}/propagate`, {
      method: "POST",
    })
  );
}

export async function updatePriceTable(
  id: string,
  input: {
    name: string;
    scope: ClientType;
    active: boolean;
    entries?: PriceTableEntry[];
  },
): Promise<PriceTableDetail> {
  const priceTable = await requestJson<PriceTableDetailDto>(`/api/v1/admin/price-tables/${id}`, {
    method: "PUT",
    body: {
      ...input,
      entries: input.entries ? toEntries(input.entries) : undefined,
    } satisfies PriceTableUpsertDto,
  });
  return toPriceTableDetail(priceTable);
}

export async function migrateCustomers(
  sourceTableId: string,
  input: { targetTableId: string; customerIds: string[] },
): Promise<PriceTableDetail> {
  return toPriceTableDetail(
    await requestJson<PriceTableDetailDto>(`/api/v1/admin/price-tables/${sourceTableId}/migrate-customers`, {
      method: "POST",
      body: input,
    })
  );
}

export async function deletePriceTable(id: string): Promise<void> {
  await requestJson<void>(`/api/v1/admin/price-tables/${id}`, {
    method: "DELETE",
  });
}

/** Histórico somente leitura das até 4 últimas alterações de preço de uma entrada (mais recentes primeiro). */
export async function fetchEntryPriceHistory(tableId: string, entryId: string): Promise<PriceChange[]> {
  const changes = await requestJson<PriceChangeDto[]>(
    `/api/v1/admin/price-tables/${tableId}/entries/${entryId}/price-history`,
  );
  return changes.map((c) => ({
    oldPrice: c.oldPrice,
    newPrice: c.newPrice,
    changedAt: c.changedAt,
    actorEmail: c.actorEmail,
    actorRole: c.actorRole,
  }));
}

/** GET que retorna um arquivo (PDF/XLSX) e dispara o download no navegador. */
async function downloadPriceTableFile(path: string, fallbackName: string): Promise<void> {
  const response = await fetch(`${getApiBaseUrl()}${path}`, {
    method: "GET",
    credentials: "include",
  });

  if (!response.ok) {
    let message = "Falha ao exportar a tabela de preço";
    try {
      const data = await response.json();
      message = data?.message ?? message;
    } catch {
      // resposta nao-JSON
    }
    // Rota ainda indisponivel no backend (deploy em andamento): 404, ou o 500 generico
    // "Unexpected error" que o Spring Boot lanca para NoHandlerFoundException.
    if (response.status === 404 || message === "Unexpected error") {
      message = "Publicação em andamento, tente novamente em instantes";
    }
    throw new Error(message);
  }

  const blob = await response.blob();
  const disposition = response.headers.get("Content-Disposition") ?? "";
  const match = disposition.match(/filename="?([^";]+)"?/i);
  const filename = match?.[1] ?? fallbackName;

  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

export async function exportPriceTablePdf(id: string): Promise<void> {
  await downloadPriceTableFile(`/api/v1/admin/price-tables/${id}/export.pdf`, "tabela-preco.pdf");
}

export async function exportPriceTableXlsx(id: string): Promise<void> {
  await downloadPriceTableFile(`/api/v1/admin/price-tables/${id}/export.xlsx`, "tabela-preco.xlsx");
}

/**
 * Comparativo de preços de um segmento (Tabela Padrão x personalizadas ativas) em XLSX consolidado —
 * uma aba, um par [preço, diferença] por tabela personalizada. Somente leitura.
 */
export async function exportSegmentComparisonXlsx(scope: ClientType): Promise<void> {
  await downloadPriceTableFile(
    `/api/v1/admin/price-tables/segment-comparison/export.xlsx?scope=${encodeURIComponent(scope)}`,
    `comparativo-precos-${scope.toLowerCase()}.xlsx`,
  );
}
